import {
  SampleSpecRow,
  environmentTypeOptions,
  formatIsoDate,
  formatNumericValue,
  formatRangeForTooltip,
  formatSampleTypeLabel,
  formatSamplingPointSubTypeLabel,
  getSubtypeValuesForType,
  parseCoordinatesCell,
  parseDateCell,
  parseNumberCell,
  parseTextCell,
  sampleSheetColumnMetadata,
  toSampleType,
} from '@resistapp/common/sample-sheet';
import { getCountryNameByAlpha3, type CountryByAlpha3 } from '@resistapp/common/types';
import type {
  CellClassParams,
  CellClassRules,
  ColDef,
  EditableCallbackParams,
  ICellEditorParams,
  ICellRendererParams,
  IMultiFilterParams,
  IRowNode,
  ITooltipParams,
  ValueFormatterParams,
  ValueGetterParams,
  ValueParserParams,
  ValueSetterParams,
} from 'ag-grid-community';
import {
  buildDropdownColDef,
  createDropdownValueSnapper,
  type CellIssueLookup,
  type DropdownPolicy,
  type DropdownRowMutation,
  type DropdownSnapOptions,
} from './dropdown-field-factory';
import { DropdownIndicatorRenderer } from './dropdown-indicator-renderer';
import { normalizeSpaces } from './dropdown-matching';
import type { FreeTextDropdownEditorConfig } from './free-text-dropdown-editor';
import {
  applySamplingPointFieldsToRow,
  applySamplingPointToRow,
  newSamplingPointSentinelLabel,
  type NormalizedSamplingPointFields,
  type SampleSheetRowDraft,
  type SamplingPointDraftState,
  type SamplingPointSamplingPointData,
} from './sample-sheet-data';

export type { CellIssueLookup } from './dropdown-field-factory';

export type CellTouchedLookup = (rowId: string, field: keyof SampleSheetRowDraft) => boolean;

export type CellWarningLookup = (rowId: string, field: keyof SampleSheetRowDraft) => string[] | null;

type ColumnDataType = 'text' | 'number' | 'date' | 'coordinates';

type ColumnCategory = 'samplingPoint' | 'sample';

type ColumnConfig = {
  key: keyof SampleSpecRow;
  headerName: string;
  description?: string;
  width?: number;
  minWidth?: number;
  type: ColumnDataType;
  cellEditor?: ColDef<SampleSheetRowDraft>['cellEditor'];
  cellEditorParams?: ColDef<SampleSheetRowDraft>['cellEditorParams'];
  editable?: boolean;
  cellClass?: string;
  headerClass?: string;
  category?: ColumnCategory;
  initialHide?: boolean;
  initialPinned?: 'left' | 'right';
};

const emptyOption = '';

type ColumnBuildOptions = {
  getCellIssues?: CellIssueLookup;
  getCellWarnings?: CellWarningLookup;
  isCellPending?: (rowId: string, field: keyof SampleSheetRowDraft) => boolean;
  isCellCorrected?: (rowId: string, field: keyof SampleSheetRowDraft) => boolean;
  countriesByAlpha3?: CountryByAlpha3;
  isRowReadOnly?: (row: SampleSheetRowDraft) => boolean;
  canEditSubmittedSamplingPoint?: (rowId: string, samplingPointId: number | null) => boolean;
  activeFilterFields?: ReadonlySet<string>;
  isCellTouched?: CellTouchedLookup;
  getSamplingPointDraftState?: (rowId: string) => SamplingPointDraftState | null;
  getSamplingPointHighlightState?: (rowId: string) => ('existing' | 'new') | null;
  dilutedVolumeMin?: number | null;
  primaryDnaDetailsOptional?: boolean;
  asteriskedHeaderFields?: ReadonlySet<keyof SampleSpecRow>;
  samplingPointOptions?: {
    sentinelLabel: string;
    labels: string[];
    labelToSmplPntId: Record<string, number | undefined>;
    labelBySmplPntId: Record<number, string | undefined>;
    envIdsByName: Record<string, number[] | undefined>;
    envById: Record<number, SamplingPointSamplingPointData | undefined>;
    draftByKey: Record<string, NormalizedSamplingPointFields | undefined>;
    draftKeyByLabel: Record<string, string | undefined>;
  };
  shouldSuppressDropdownCanonicalSelect?: () => boolean;
  onDropdownRowMutation?: (row: SampleSheetRowDraft, changes: DropdownRowMutation[]) => void;
};

const fallbackSamplingPointOptions: NonNullable<ColumnBuildOptions['samplingPointOptions']> = {
  sentinelLabel: newSamplingPointSentinelLabel,
  labels: [],
  labelToSmplPntId: {},
  labelBySmplPntId: {},
  envIdsByName: {},
  envById: {},
  draftByKey: {},
  draftKeyByLabel: {},
};

function createMultiFilterParams(type: ColumnDataType): IMultiFilterParams {
  if (type === 'number') {
    return {
      filters: [{ filter: 'agNumberColumnFilter' }, { filter: 'agSetColumnFilter' }],
    } satisfies IMultiFilterParams;
  }
  if (type === 'date') {
    return {
      filters: [{ filter: 'agDateColumnFilter' }, { filter: 'agSetColumnFilter' }],
    } satisfies IMultiFilterParams;
  }
  return {
    filters: [{ filter: 'agTextColumnFilter' }, { filter: 'agSetColumnFilter' }],
  } satisfies IMultiFilterParams;
}

const defaultSetFilterValueFormatter = (params: ValueFormatterParams<SampleSheetRowDraft>) =>
  params.value == null ? '' : String(params.value);

const normalizeFilterParams = (params: unknown): Record<string, unknown> =>
  params && typeof params === 'object' ? (params as Record<string, unknown>) : {};

function withSetFilterValueFormatter(
  filterParams: IMultiFilterParams | undefined,
  valueFormatter: ColDef<SampleSheetRowDraft>['valueFormatter'] | undefined,
): IMultiFilterParams | undefined {
  const { filters } = filterParams ?? {};
  if (!filters?.length) {
    return filterParams;
  }

  const resolvedValueFormatter =
    typeof valueFormatter === 'function' ? valueFormatter : defaultSetFilterValueFormatter;

  const nextFilters = filters.map(filter => {
    if (filter.filter !== 'agSetColumnFilter') {
      return filter;
    }
    const existingParams = normalizeFilterParams(filter.filterParams);
    if ('valueFormatter' in existingParams) {
      return filter;
    }
    return {
      ...filter,
      filterParams: {
        ...existingParams,
        valueFormatter: resolvedValueFormatter,
      },
    };
  });

  return {
    ...filterParams,
    filters: nextFilters,
  };
}

const columnConfigs: ColumnConfig[] = [
  {
    key: 'sampleDescription',
    headerName: 'Sampling point description',
    type: 'text',
    width: 170,
    category: 'samplingPoint',
  },
  {
    key: 'sampleType',
    headerName: 'Sample type\n\u00a0',
    type: 'text',
    width: 150,
    cellEditor: 'agSelectCellEditor',
    category: 'samplingPoint',
  },
  {
    key: 'detailedSampleType',
    headerName: 'Detailed\nsample type',
    type: 'text',
    width: 150,
    cellEditor: 'agSelectCellEditor',
    category: 'samplingPoint',
  },
  {
    key: 'country',
    headerName: 'Country\n\u00a0',
    description: 'Select a country. The saved value uses ISO alpha-3 codes (e.g. FIN).',
    type: 'text',
    width: 120,
    minWidth: 70,
    category: 'samplingPoint',
  },
  {
    key: 'stateOrRegion',
    headerName: 'State\nor region',
    type: 'text',
    width: 120,
    minWidth: 100,
    category: 'samplingPoint',
  },
  { key: 'city', headerName: 'City\n\u00a0', type: 'text', width: 90, minWidth: 80, category: 'samplingPoint' },
  {
    key: 'latitudeLongitude',
    headerName: 'Latitude, longitude\n(eg. 60.170451, 24.939575)',
    type: 'coordinates',
    width: 190,
    minWidth: 150,
    category: 'samplingPoint',
  },
  {
    key: 'sampleId',
    headerName: 'Sample\nID',
    type: 'number',
    width: 107,
    minWidth: 80,
    editable: false,
    cellClass: 'sample-sheet-readonly-cell',
    category: 'sample',
    initialHide: true,
  },
  {
    key: 'sampleCollectionDate',
    headerName: 'Sample collection date',
    type: 'date',
    width: 130,
    minWidth: 100,
    category: 'sample',
  },
  {
    key: 'primDnaExtractionKit',
    headerName: 'DNA extraction kit',
    type: 'text',
    minWidth: 100,
    width: 130,
    category: 'sample',
  },
  {
    key: 'flowRate',
    headerName: 'Flow rate\n(m3/d)',
    type: 'number',
    width: 100,
    category: 'sample',
    initialHide: true,
  },
  {
    key: 'operatedFlowRate',
    headerName: 'Operated flow rate\n(m3/d)',
    type: 'number',
    width: 190,
    category: 'sample',
    initialHide: true,
  },
  {
    key: 'suspendedSolids',
    headerName: 'Suspended Solids\n(mg/L)',
    type: 'number',
    width: 180,
    category: 'sample',
    initialHide: true,
  },
  {
    key: 'bod',
    headerName: 'BOD\n(mg/L)',
    type: 'number',
    width: 110,
    minWidth: 80,
    category: 'sample',
    initialHide: true,
  },
  {
    key: 'filteredVolume',
    headerName: 'Filtered Volume\n(mL)',
    type: 'number',
    width: 160,
    category: 'sample',
  },
  {
    key: 'primElutedDnaVolume',
    headerName: 'Eluted DNA volume\n(µL)',
    type: 'number',
    width: 190,
    category: 'sample',
  },
  {
    key: 'primElutedDnaConcentration',
    headerName: 'Eluted DNA\nconcentration\n(ng/µl)',
    type: 'number',
    width: 150,
    category: 'sample',
  },
  {
    key: 'primElutedDnaQuality280',
    headerName: 'Eluted DNA\nquality 260/280\n(1.8 – 2 ±0.1)',
    type: 'number',
    width: 170,
    category: 'sample',
  },
  {
    key: 'primElutedDnaQuality230',
    headerName: 'Eluted DNA\nquality 260/230',
    type: 'number',
    width: 170,
    category: 'sample',
  },
  {
    key: 'primDilutedDnaConcentration',
    headerName: 'Diluted DNA\nconcentration\n(must be 10 ng/µl)',
    type: 'number',
    width: 180,
    category: 'sample',
  },
  {
    key: 'primDilutedDnaQuality280',
    headerName: 'Diluted DNA\nquality 260/280\n(1.8 – 2 ±0.1)',
    type: 'number',
    width: 170,
    category: 'sample',
  },
  {
    key: 'primDilutedDnaQuality230',
    headerName: 'Diluted DNA\nquality 260/230',
    type: 'number',
    width: 170,
    category: 'sample',
  },
  {
    key: 'primDilutedDnaVolume',
    headerName: 'Diluted DNA volume\n(µL)',
    type: 'number',
    width: 200,
    category: 'sample',
  },
];

export function buildSampleSheetColumnDefs(options: ColumnBuildOptions = {}): {
  columnDefs: Array<ColDef<SampleSheetRowDraft>>;
  snapDropdownValue: (
    field: keyof SampleSheetRowDraft,
    raw: string,
    row: SampleSheetRowDraft,
    options?: DropdownSnapOptions,
  ) => string;
} {
  const getCellIssues = options.getCellIssues ?? (() => null);
  const getCellWarnings = options.getCellWarnings ?? (() => null);
  const isCellPending = options.isCellPending ?? (() => false);
  const isCellCorrected = options.isCellCorrected ?? (() => false);
  const isCellTouched = options.isCellTouched ?? (() => true);
  const countryNameByAlpha3 = options.countriesByAlpha3 ? getCountryNameByAlpha3(options.countriesByAlpha3) : {};
  const countryEntries: Array<[string, string]> = Object.keys(countryNameByAlpha3).map(alpha3 => [
    alpha3,
    countryNameByAlpha3[alpha3],
  ]);
  countryEntries.sort(([, aName], [, bName]) => aName.localeCompare(bName));
  const countryEditorValues = [...countryEntries.map(([alpha3]) => alpha3), emptyOption];
  const isRowReadOnly = options.isRowReadOnly ?? (() => false);
  const canEditSubmittedSamplingPoint =
    typeof options.canEditSubmittedSamplingPoint === 'function' ? options.canEditSubmittedSamplingPoint : () => false;
  const activeFilterFields = options.activeFilterFields ?? new Set<string>();
  const samplingPointOptions = options.samplingPointOptions ?? fallbackSamplingPointOptions;
  const shouldSuppressDropdownCanonicalSelect =
    typeof options.shouldSuppressDropdownCanonicalSelect === 'function'
      ? options.shouldSuppressDropdownCanonicalSelect
      : () => false;
  const dilutedVolumeMin =
    typeof options.dilutedVolumeMin === 'number' &&
    Number.isFinite(options.dilutedVolumeMin) &&
    options.dilutedVolumeMin > 0
      ? options.dilutedVolumeMin
      : 100;

  const dropdownShared = {
    getCellIssues,
    isRowReadOnly,
    onDropdownRowMutation: options.onDropdownRowMutation,
    isSamplingPointColumn: true, // All dropdown columns are sampling point columns
  } as const;

  const sampleDescriptionPolicy: DropdownPolicy = {
    options: () => samplingPointOptions.labels,
    snapMode: 'exact-only',
    canonicalMatchMode: 'case-sensitive',
    onRawValue: (row, _value) => {
      if (row.isSubmittedSample) {
        return row;
      }
      if (row.samplingPointId != null) {
        row.samplingPointId = null;
      }
      return row;
    },
    onCanonicalSelect: (row, canonical) => {
      const matchedSmplPntId = samplingPointOptions.labelToSmplPntId[canonical];
      if (matchedSmplPntId != null) {
        const samplingPoint = samplingPointOptions.envById[matchedSmplPntId];
        if (samplingPoint) {
          const updatedRow = applySamplingPointToRow(row, samplingPoint);
          if (updatedRow !== row) {
            return updatedRow;
          }
        }
      }
      const draftKey = samplingPointOptions.draftKeyByLabel[canonical];
      if (draftKey) {
        const draftFields = samplingPointOptions.draftByKey[draftKey];
        if (draftFields) {
          return applySamplingPointFieldsToRow(row, draftFields, null);
        }
      }
      return row;
    },
  };

  const sampleTypeOptions = [...environmentTypeOptions, emptyOption];
  const sampleTypePolicy: DropdownPolicy = {
    options: () => sampleTypeOptions,
    formatOption: value => formatSampleTypeLabel(value),
    snapMode: 'unique-substring',
    onRawValue: (row, rawValue) => {
      const prevRaw = typeof row.sampleType === 'string' ? row.sampleType : '';
      const nextRaw = rawValue ?? '';
      const prevNorm = normalizeSpaces(String(prevRaw)).trim().toLowerCase();
      const nextNorm = normalizeSpaces(String(nextRaw)).trim().toLowerCase();

      // If the type didn’t effectively change, keep subtype as-is.
      if (prevNorm === nextNorm) {
        return row;
      }

      if (row.isSubmittedSample) {
        return row;
      }

      // Type changed (or was cleared) → drop any existing sampling point link.
      // Keep the current subtype so validation can highlight mismatches instead of silently discarding the value.
      return {
        ...row,
        samplingPointId: null,
      };
    },
  };

  const allDetailedSampleTypeOptions = [...getSubtypeValuesForType(null), emptyOption];
  const detailedSampleTypePolicy: DropdownPolicy = {
    options: () => allDetailedSampleTypeOptions,
    optionsForRow: row => {
      const sampleType = toSampleType(row.sampleType);
      return [...getSubtypeValuesForType(sampleType), emptyOption];
    },
    formatOption: (value, row) => formatSamplingPointSubTypeLabel(value, toSampleType(row?.sampleType)),
    snapMode: 'unique-substring',
  };

  const countryPolicy: DropdownPolicy = {
    options: () => countryEditorValues,
    formatOption: value => formatCountryValue(value, countryNameByAlpha3),
    snapMode: 'unique-substring',
  };

  const dropdownPolicies: Partial<Record<keyof SampleSheetRowDraft, DropdownPolicy>> = {
    sampleDescription: sampleDescriptionPolicy,
    sampleType: sampleTypePolicy,
    detailedSampleType: detailedSampleTypePolicy,
    country: countryPolicy,
  };

  const dropdownSnappers = new Map<
    keyof SampleSheetRowDraft,
    (row: SampleSheetRowDraft, raw: string, options?: DropdownSnapOptions) => string
  >();
  (Object.entries(dropdownPolicies) as Array<[keyof SampleSheetRowDraft, DropdownPolicy | undefined]>).forEach(
    ([key, policy]) => {
      if (policy) {
        dropdownSnappers.set(key, createDropdownValueSnapper(policy, options.onDropdownRowMutation));
      }
    },
  );

  const snapDropdownValue = (
    field: keyof SampleSheetRowDraft,
    raw: string,
    row: SampleSheetRowDraft,
    snapOptions?: DropdownSnapOptions,
  ): string => {
    const snapper = dropdownSnappers.get(field);
    if (!snapper) {
      return raw;
    }
    const suppressCanonical = shouldSuppressDropdownCanonicalSelect();
    const mergedOptions =
      suppressCanonical && !(snapOptions?.suppressCanonicalSelect ?? false)
        ? { ...(snapOptions ?? {}), suppressCanonicalSelect: true }
        : snapOptions;
    return snapper(row, raw, mergedOptions);
  };

  const dataColumns = columnConfigs.map(config => {
    const fieldKey = config.key as keyof SampleSheetRowDraft;
    const baseDef: ColDef<SampleSheetRowDraft> = {
      headerName: config.headerName,
      field: fieldKey,
      colId: String(config.key),
      wrapHeaderText: true,
      autoHeaderHeight: true,
      width: config.width,
    };

    if (config.initialHide) {
      baseDef.initialHide = true;
    }
    if (config.initialPinned) {
      baseDef.initialPinned = config.initialPinned;
    }

    baseDef.suppressHeaderFilterButton = !activeFilterFields.has(String(config.key));

    baseDef.filterParams = createMultiFilterParams(config.type);

    if (config.minWidth != null) {
      baseDef.minWidth = config.minWidth;
    }

    const dropdownPolicy = dropdownPolicies[fieldKey];
    if (dropdownPolicy) {
      Object.assign(baseDef, buildDropdownColDef(fieldKey, config.headerName, dropdownPolicy, dropdownShared));
    }

    if (typeof config.cellEditor === 'string' && baseDef.cellEditor == null) {
      baseDef.cellEditor = config.cellEditor;
    }

    if (config.cellClass) {
      baseDef.cellClass = config.cellClass;
    }

    const isSamplingPointColumn = config.category === 'samplingPoint';
    if (config.editable === false) {
      baseDef.editable = false;
    } else if (baseDef.editable == null) {
      baseDef.editable = (params: EditableCallbackParams<SampleSheetRowDraft>) => {
        const data = params.data;
        if (!data) {
          return false;
        }
        if (isRowReadOnly(data)) {
          return false;
        }
        return true;
      };
    }

    if (isSamplingPointColumn) {
      const baseEditable = baseDef.editable;
      baseDef.editable = (params: EditableCallbackParams<SampleSheetRowDraft>) => {
        const data = params.data;
        if (!data) {
          return false;
        }
        if (data.isSubmittedSample) {
          return canEditSubmittedSamplingPoint(data.rowId, data.samplingPointId ?? null);
        }

        if (typeof baseEditable === 'function') {
          return baseEditable(params);
        }
        return baseEditable && !isRowReadOnly(data);
      };
    }

    if (config.type === 'number') {
      baseDef.valueParser = numberValueParser;
      baseDef.valueFormatter = numberValueFormatter;
      baseDef.filterValueGetter = params => getNumericFilterValue(params.data ? params.data[fieldKey] : null);
      baseDef.enableValue = false;
      delete baseDef.allowedAggFuncs;
      baseDef.comparator = numberComparator;
    } else if (config.type === 'date') {
      // Treat as text so AG Grid never blocks "wrong data type" edits; validation runs separately.
      baseDef.cellDataType = 'text';
      baseDef.valueParser = dateValueParser;
      baseDef.valueFormatter = dateValueFormatter;
      baseDef.filterValueGetter = params => getDateFilterValue(params.data ? params.data[fieldKey] : null);
      baseDef.comparator = dateComparator;
    } else if (config.type === 'coordinates') {
      baseDef.cellDataType = 'object';
      baseDef.valueParser = coordinatesValueParser;
      baseDef.valueFormatter = coordinatesValueFormatter;
      baseDef.valueSetter = coordinatesValueSetter;
      baseDef.equals = (left, right) =>
        coordinatesDraftEquals(
          left as SampleSheetRowDraft['latitudeLongitude'],
          right as SampleSheetRowDraft['latitudeLongitude'],
        );
      baseDef.filterValueGetter = (params: ValueGetterParams<SampleSheetRowDraft, unknown>) =>
        formatCoordinatesForDisplay(params.data?.latitudeLongitude);
      baseDef.keyCreator = params => formatCoordinatesForDisplay(params.value);
      baseDef.comparator = (
        left: SampleSpecRow['latitudeLongitude'] | null | undefined,
        right: SampleSpecRow['latitudeLongitude'] | null | undefined,
      ) => compareCoordinateValues(left, right);
    } else if (!dropdownPolicy) {
      baseDef.cellDataType = 'text';
      baseDef.valueParser = textValueParser;
      baseDef.valueFormatter = textValueFormatter;
    }

    if (config.key === 'sampleNumber') {
      baseDef.valueFormatter = (params: ValueFormatterParams<SampleSheetRowDraft>) =>
        formatSampleNumberWithBioReplica(params.data);
      baseDef.comparator = sampleNumberWithReplicaComparator;
    }

    if (config.key === 'sampleDescription') {
      const originalEditorParams = baseDef.cellEditorParams as
        | undefined
        | FreeTextDropdownEditorConfig
        | ((params: ICellEditorParams<SampleSheetRowDraft | undefined, unknown>) => FreeTextDropdownEditorConfig);
      const originalEditorParamsFn =
        typeof originalEditorParams === 'function'
          ? (originalEditorParams as (
              params: ICellEditorParams<SampleSheetRowDraft | undefined, unknown>,
            ) => FreeTextDropdownEditorConfig)
          : undefined;
      const originalEditorParamsObject = typeof originalEditorParams === 'function' ? undefined : originalEditorParams;
      baseDef.cellEditorParams = (params: ICellEditorParams<SampleSheetRowDraft | undefined, unknown>) => {
        const baseConfig: FreeTextDropdownEditorConfig = originalEditorParamsFn
          ? originalEditorParamsFn(params)
          : originalEditorParamsObject ?? {};
        return {
          ...baseConfig,
          placeholder: 'Type to search or create',
          createSectionLabel: 'Create a new sampling point',
          existingSectionLabel: 'Select an existing sampling point',
        };
      };

      const stateResolver = options.getSamplingPointDraftState;
      if (stateResolver) {
        if (typeof baseDef.cellRendererParams === 'function') {
          const rendererParamsFn = baseDef.cellRendererParams as (
            params: ICellRendererParams<SampleSheetRowDraft | undefined, unknown>,
          ) => unknown;
          baseDef.cellRendererParams = (params: ICellRendererParams<SampleSheetRowDraft | undefined, unknown>) => {
            const derived = rendererParamsFn(params);
            const baseParams = derived && typeof derived === 'object' ? (derived as Record<string, unknown>) : {};
            return {
              ...baseParams,
              getSamplingPointDraftState: stateResolver,
            } satisfies Record<string, unknown>;
          };
        } else if (baseDef.cellRendererParams && typeof baseDef.cellRendererParams === 'object') {
          baseDef.cellRendererParams = {
            ...(baseDef.cellRendererParams as Record<string, unknown>),
            getSamplingPointDraftState: stateResolver,
          } satisfies Record<string, unknown>;
        } else {
          baseDef.cellRendererParams = { getSamplingPointDraftState: stateResolver } satisfies Record<string, unknown>;
        }
      }
    }

    const hasDropdownIndicator =
      dropdownPolicy != null ||
      (typeof baseDef.cellEditor === 'string' &&
        (baseDef.cellEditor === 'agSelectCellEditor' || baseDef.cellEditor === 'agRichSelectCellEditor'));
    if (hasDropdownIndicator) {
      baseDef.cellRenderer = DropdownIndicatorRenderer;
    }

    const metaKey = fieldKey as keyof SampleSpecRow;
    const columnMeta = sampleSheetColumnMetadata[metaKey];
    const headerLabel = columnMeta.header;
    if (headerLabel) {
      baseDef.headerName = headerLabel;
    }

    if (metaKey === 'primDilutedDnaVolume' && !options.primaryDnaDetailsOptional) {
      baseDef.headerName = `Diluted DNA volume\n(required \u2265${formatNumericValue(dilutedVolumeMin)} \u00b5L)`;
    }

    if (baseDef.headerName && options.asteriskedHeaderFields?.has(metaKey)) {
      baseDef.headerName = addHeaderAsterisk(baseDef.headerName);
    }

    let columnTooltip = buildColumnTooltip(metaKey, config.description);
    if (metaKey === 'primDilutedDnaVolume' && !options.primaryDnaDetailsOptional) {
      const requiredText =
        dilutedVolumeMin === 100
          ? 'Requires at least 100 \u00b5L by default (until a panel/layout is selected).'
          : `Requires at least ${formatNumericValue(dilutedVolumeMin)} \u00b5L for the selected panel/layout.`;
      columnTooltip = requiredText;
    }
    baseDef.headerTooltip = columnTooltip ?? undefined;
    baseDef.tooltipValueGetter = (params: ITooltipParams<SampleSheetRowDraft, unknown>) => {
      const data = params.data;
      const rowId = data?.rowId;
      if (!rowId) {
        return undefined;
      }
      const issues = getCellIssues(rowId, fieldKey) ?? [];
      const touched = isCellTouched(rowId, fieldKey);
      if (issues.length > 0 && touched) {
        return issues.join('\n');
      }
      const warnings = getCellWarnings(rowId, fieldKey) ?? [];
      if (warnings.length > 0) {
        const warningText = warnings.join('\n');
        if (touched) {
          if (columnTooltip) {
            return `${warningText}\n\n${columnTooltip}`;
          }
          return warningText;
        }
        return columnTooltip ?? warningText;
      }
      return undefined;
    };

    const highlightResolver = config.category === 'samplingPoint' ? options.getSamplingPointHighlightState : undefined;

    const classNames: string[] = [];
    if (config.cellClass) {
      classNames.push(config.cellClass);
    }
    if (config.category === 'samplingPoint') {
      classNames.push('sample-sheet-sampling-point-cell');
    } else if (config.category === 'sample') {
      classNames.push('sample-sheet-sample-cell');
    }
    if (classNames.length > 0) {
      baseDef.cellClass = classNames.join(' ');
    }

    const headerClassNames: string[] = [];
    if (config.headerClass) {
      headerClassNames.push(config.headerClass);
    }
    if (config.category === 'samplingPoint') {
      headerClassNames.push('sample-sheet-sampling-point-header');
    } else if (config.category === 'sample') {
      headerClassNames.push('sample-sheet-sample-header');
    }
    if (headerClassNames.length > 0) {
      baseDef.headerClass = headerClassNames.join(' ');
    }

    const highlightRules: CellClassRules<SampleSheetRowDraft> = highlightResolver
      ? {
          'sample-sheet-sampling-point-highlight-existing': (params: CellClassParams<SampleSheetRowDraft, unknown>) => {
            const rowId = params.data?.rowId;
            if (!rowId) {
              return false;
            }
            return highlightResolver(rowId) === 'existing';
          },
          'sample-sheet-sampling-point-highlight-new': (params: CellClassParams<SampleSheetRowDraft, unknown>) => {
            const rowId = params.data?.rowId;
            if (!rowId) {
              return false;
            }
            return highlightResolver(rowId) === 'new';
          },
        }
      : {};

    baseDef.cellClassRules = {
      ...baseDef.cellClassRules,
      ...highlightRules,
      'sample-sheet-invalid-cell': (params: CellClassParams<SampleSheetRowDraft, unknown>) => {
        const data = params.data;
        const rowId = data?.rowId;
        if (!rowId) {
          return false;
        }
        if (!isCellTouched(rowId, fieldKey)) {
          return false;
        }
        const issues = getCellIssues(rowId, fieldKey) ?? [];
        return issues.length > 0;
      },
      'sample-sheet-warning-cell': (params: CellClassParams<SampleSheetRowDraft, unknown>) => {
        const data = params.data;
        const rowId = data?.rowId;
        if (!rowId) {
          return false;
        }
        if (!isCellTouched(rowId, fieldKey)) {
          return false;
        }
        const warnings = getCellWarnings(rowId, fieldKey) ?? [];
        return warnings.length > 0;
      },
      'sample-sheet-warning-indicator': (params: CellClassParams<SampleSheetRowDraft, unknown>) => {
        const data = params.data;
        const rowId = data?.rowId;
        if (!rowId) {
          return false;
        }
        const warnings = getCellWarnings(rowId, fieldKey) ?? [];
        const issues = getCellIssues(rowId, fieldKey) ?? [];
        // Show the soft triangle only when there are warnings and no hard issues
        return warnings.length > 0 && issues.length === 0;
      },
      'sample-sheet-pending-cell': (params: CellClassParams<SampleSheetRowDraft, unknown>) => {
        const data = params.data;
        if (!data) {
          return false;
        }
        return isCellPending(data.rowId, fieldKey);
      },
      'sample-sheet-corrected-cell': (params: CellClassParams<SampleSheetRowDraft, unknown>) => {
        const data = params.data;
        if (!data) {
          return false;
        }
        return isCellCorrected(data.rowId, fieldKey);
      },
      // Show readonly styling for sampling point columns when editing submitted samples
      // when the linked sampling point isn't editable (shared/missing usage/special cases)
      'sample-sheet-readonly-cell': (params: CellClassParams<SampleSheetRowDraft, unknown>) => {
        const data = params.data;
        if (!data) {
          return false;
        }
        if (!isSamplingPointColumn || data.isSubmittedSample !== true || data.isReadOnly) {
          return false;
        }
        return !canEditSubmittedSamplingPoint(data.rowId, data.samplingPointId ?? null);
      },
    };

    if (baseDef.keyCreator) {
      baseDef.filterParams = withSetFilterValueFormatter(
        baseDef.filterParams as IMultiFilterParams | undefined,
        baseDef.valueFormatter,
      );
    }

    return baseDef;
  });

  return {
    columnDefs: dataColumns,
    snapDropdownValue,
  };
}

function numberValueParser(params: ValueParserParams<SampleSheetRowDraft, unknown>): number | string | null {
  const nextValue: unknown = params.newValue;
  if (typeof nextValue === 'number' && Number.isFinite(nextValue)) {
    return nextValue;
  }
  if (typeof nextValue === 'string') {
    const trimmed = nextValue.trim();
    return trimmed === '' ? null : trimmed;
  }
  return (params.oldValue ?? null) as number | string | null;
}

function numberValueFormatter(params: ValueFormatterParams<SampleSheetRowDraft>) {
  const value: unknown = params.value;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === 'string') {
    return value;
  }
  return '';
}

function textValueParser(params: ValueParserParams<SampleSheetRowDraft, unknown>): string | null {
  const parsed = parseTextCell(params.newValue);
  if (parsed !== undefined) {
    return parsed;
  }
  return typeof params.oldValue === 'string' ? params.oldValue : null;
}

function textValueFormatter(params: ValueFormatterParams<SampleSheetRowDraft>) {
  return typeof params.value === 'string' ? params.value : '';
}

function dateValueParser(params: ValueParserParams<SampleSheetRowDraft, unknown>): string | Date | null {
  const nextValue: unknown = params.newValue;
  if (nextValue instanceof Date) {
    return nextValue;
  }
  if (typeof nextValue === 'string') {
    const trimmed = nextValue.trim();
    return trimmed === '' ? null : trimmed;
  }
  if (typeof nextValue === 'number' && Number.isFinite(nextValue)) {
    return String(nextValue);
  }
  return (params.oldValue ?? null) as string | Date | null;
}

function dateValueFormatter(params: ValueFormatterParams<SampleSheetRowDraft>) {
  const value: unknown = params.value;
  if (typeof value === 'string') {
    return value;
  }
  const parsed = parseDateCell(value);
  return parsed ? formatIsoDate(parsed) : '';
}

function getNumericFilterValue(value: SampleSheetRowDraft[keyof SampleSheetRowDraft]): number | null {
  const parsed = parseNumberCell(value);
  if (parsed === undefined || parsed === null || Number.isNaN(parsed)) {
    return null;
  }
  return parsed;
}

function getDateFilterValue(value: SampleSheetRowDraft[keyof SampleSheetRowDraft]): Date | null {
  const parsed = parseDateCell(value);
  return parsed ?? null;
}

function formatSampleNumberWithBioReplica(row: SampleSheetRowDraft | null | undefined): string {
  if (!row) {
    return '';
  }
  const sampleNumber = parseNumberCell(row.sampleNumber);
  const bioReplica = normalizeBioReplicaForDisplay(row.bioReplica);
  if (sampleNumber == null) {
    return bioReplica ?? '';
  }
  return bioReplica ? `${sampleNumber} ${bioReplica}` : String(sampleNumber);
}

function sampleNumberWithReplicaComparator(
  left: SampleSheetRowDraft['sampleNumber'],
  right: SampleSheetRowDraft['sampleNumber'],
  nodeA?: IRowNode<SampleSheetRowDraft>,
  nodeB?: IRowNode<SampleSheetRowDraft>,
): number {
  const leftRow = nodeA?.data ?? null;
  const rightRow = nodeB?.data ?? null;
  const leftNumber = parseNumberCell(leftRow?.sampleNumber ?? left);
  const rightNumber = parseNumberCell(rightRow?.sampleNumber ?? right);

  if (leftNumber == null && rightNumber == null) {
    return compareBioReplicaValues(leftRow, rightRow);
  }
  if (leftNumber == null) {
    return 1;
  }
  if (rightNumber == null) {
    return -1;
  }
  if (leftNumber !== rightNumber) {
    return leftNumber - rightNumber;
  }
  return compareBioReplicaValues(leftRow, rightRow);
}

function compareBioReplicaValues(leftRow: SampleSheetRowDraft | null, rightRow: SampleSheetRowDraft | null): number {
  const leftReplica = normalizeBioReplicaForDisplay(leftRow?.bioReplica);
  const rightReplica = normalizeBioReplicaForDisplay(rightRow?.bioReplica);
  if (leftReplica === rightReplica) {
    return 0;
  }
  if (leftReplica == null) {
    return rightReplica == null ? 0 : 1;
  }
  if (rightReplica == null) {
    return -1;
  }
  return leftReplica.localeCompare(rightReplica);
}

function normalizeBioReplicaForDisplay(value: SampleSheetRowDraft['bioReplica'] | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  return trimmed.toUpperCase();
}

function coordinatesValueParser(
  params: ValueParserParams<SampleSheetRowDraft, unknown>,
): SampleSheetRowDraft['latitudeLongitude'] {
  const nextValue: unknown = params.newValue;
  if (typeof nextValue === 'string') {
    const trimmed = nextValue.trim();
    return trimmed === '' ? null : trimmed;
  }
  if (
    nextValue &&
    typeof nextValue === 'object' &&
    ('lat' in (nextValue as Record<string, unknown>) || 'lon' in (nextValue as Record<string, unknown>))
  ) {
    return cloneLatitudeLongitudeDraftValue(nextValue as SampleSheetRowDraft['latitudeLongitude']);
  }
  if (typeof nextValue === 'number' && Number.isFinite(nextValue)) {
    return String(nextValue);
  }
  return (params.oldValue ?? null) as SampleSheetRowDraft['latitudeLongitude'];
}

function coordinatesValueFormatter(params: ValueFormatterParams<SampleSheetRowDraft>) {
  const value: unknown = params.value;
  if (typeof value === 'string') {
    return value;
  }
  return formatCoordinatesForDisplay(value);
}

function formatCoordinatesForDisplay(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  const parsed = parseCoordinatesCell(value);
  if (!parsed) {
    return '';
  }
  return `${parsed.lat}, ${parsed.lon}`;
}

function cloneLatitudeLongitudeDraftValue(
  value: SampleSheetRowDraft['latitudeLongitude'] | undefined,
): SampleSheetRowDraft['latitudeLongitude'] {
  if (value == null || typeof value === 'string') {
    return value ?? null;
  }
  const { lat, lon } = value;
  return { lat, lon };
}

function coordinatesDraftEquals(
  left: SampleSheetRowDraft['latitudeLongitude'] | null | undefined,
  right: SampleSheetRowDraft['latitudeLongitude'] | null | undefined,
): boolean {
  const normalizedLeft = left ?? null;
  const normalizedRight = right ?? null;
  if (normalizedLeft === normalizedRight) {
    return true;
  }
  if (typeof normalizedLeft === 'string' || typeof normalizedRight === 'string') {
    return normalizedLeft === normalizedRight;
  }
  if (!normalizedLeft && !normalizedRight) {
    return true;
  }
  if (!normalizedLeft || !normalizedRight) {
    return false;
  }
  return normalizedLeft.lat === normalizedRight.lat && normalizedLeft.lon === normalizedRight.lon;
}

function coordinatesValueSetter(
  params: ValueSetterParams<SampleSheetRowDraft, SampleSheetRowDraft['latitudeLongitude']>,
): boolean {
  const row = params.data;
  const nextValue = cloneLatitudeLongitudeDraftValue(params.newValue ?? null);
  const currentValue = row.latitudeLongitude ?? null;
  if (coordinatesDraftEquals(currentValue, nextValue)) {
    return false;
  }
  row.latitudeLongitude = nextValue;
  return true;
}

function compareCoordinateValues(
  left: SampleSpecRow['latitudeLongitude'] | null | undefined,
  right: SampleSpecRow['latitudeLongitude'] | null | undefined,
): number {
  const normalizedLeft = parseCoordinatesCell(left);
  const normalizedRight = parseCoordinatesCell(right);
  if (!normalizedLeft && !normalizedRight) {
    return 0;
  }
  if (!normalizedLeft) {
    return -1;
  }
  if (!normalizedRight) {
    return 1;
  }
  const latitudeDelta = normalizedLeft.lat - normalizedRight.lat;
  if (latitudeDelta !== 0) {
    return latitudeDelta;
  }
  const longitudeDelta = normalizedLeft.lon - normalizedRight.lon;
  if (longitudeDelta !== 0) {
    return longitudeDelta;
  }
  return 0;
}

function numberComparator(left: unknown, right: unknown): number {
  const leftParsed = parseNumberCell(left);
  const rightParsed = parseNumberCell(right);
  const leftValue = leftParsed === undefined || leftParsed === null || Number.isNaN(leftParsed) ? null : leftParsed;
  const rightValue =
    rightParsed === undefined || rightParsed === null || Number.isNaN(rightParsed) ? null : rightParsed;

  if (leftValue === rightValue) {
    const leftLabel = left != null ? String(left) : '';
    const rightLabel = right != null ? String(right) : '';
    return leftLabel.localeCompare(rightLabel, undefined, { sensitivity: 'base' });
  }
  if (leftValue == null) {
    return rightValue == null ? 0 : 1;
  }
  if (rightValue == null) {
    return -1;
  }
  return leftValue - rightValue;
}

function dateComparator(left: unknown, right: unknown): number {
  const leftDate = parseDateCell(left);
  const rightDate = parseDateCell(right);
  const leftTime = leftDate ? leftDate.getTime() : Number.POSITIVE_INFINITY;
  const rightTime = rightDate ? rightDate.getTime() : Number.POSITIVE_INFINITY;

  if (leftTime === rightTime) {
    const leftLabel = left != null ? String(left) : '';
    const rightLabel = right != null ? String(right) : '';
    return leftLabel.localeCompare(rightLabel, undefined, { sensitivity: 'base' });
  }
  return leftTime - rightTime;
}

function formatCountryValue(value: unknown, nameByAlpha3: Record<string, string>): string {
  if (typeof value !== 'string') {
    return '';
  }
  const trimmed = normalizeSpaces(value).trim();
  if (trimmed === '') {
    return '';
  }
  const upper = trimmed.toUpperCase();
  return nameByAlpha3[upper] ?? trimmed;
}

function buildColumnTooltip(fieldKey: keyof SampleSpecRow, fallback?: string | null): string | null {
  const meta = sampleSheetColumnMetadata[fieldKey];
  const tooltipParts: string[] = [];
  if (meta.tooltip) {
    tooltipParts.push(meta.tooltip.trim());
  }
  const trimmedFallback = fallback?.trim();
  if (trimmedFallback) {
    tooltipParts.push(trimmedFallback);
  }
  const rangeTooltip = formatRangeForTooltip(fieldKey);
  if (rangeTooltip) {
    tooltipParts.push(rangeTooltip);
  }
  return tooltipParts.length > 0 ? tooltipParts.join('\n\n') : null;
}

function addHeaderAsterisk(header: string): string {
  if (header.trim().length === 0) {
    return header;
  }

  if (header.includes('*')) {
    return header;
  }

  const insertBeforeIndex = (index: number) => {
    let insertAt = index;
    while (insertAt > 0 && /\s/.test(header[insertAt - 1])) {
      insertAt -= 1;
    }
    return `${header.slice(0, insertAt)}*${header.slice(insertAt)}`;
  };

  const parenIndex = header.indexOf('(');
  if (parenIndex !== -1) {
    return insertBeforeIndex(parenIndex);
  }

  return insertBeforeIndex(header.length);
}
